import { useState } from "react";
import {
  useGetMyFirmCommitteeReviewsQuery,
  useDecideFirmCommitteeReviewMutation,
} from "../api/firmApi";
import FirmStatusBadge from "../components/FirmStatusBadge";
import FirmPersonCard from "../components/FirmPersonCard";
import { firmStorageUrl } from "../utils";
import { describeApiError } from "../../../lib/apiError";
import {
  Loader2, Building2, CheckCircle2, XCircle, Clock, RefreshCw, AlertCircle, FileText,
} from "lucide-react";

/**
 * Firm registrations assigned to a Membership Committee member.
 *
 * Unlike an upgrade review, the decision here is final in itself: approving
 * sends the application forward for interview, returning it sends it back to
 * the secretariat with a message. There is no chairman behind it, so the whole
 * application travels with the assignment and is laid out in full.
 */

const DECISION_ICON: Record<string, JSX.Element> = {
  pending: <Clock className="w-4 h-4 text-amber-500" />,
  approved: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  rejected: <XCircle className="w-4 h-4 text-red-500" />,
};

function Row({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{String(value)}</span>
    </div>
  );
}

function ReviewCard({ review, onDone }: { review: any; onDone: () => void }) {
  const app = review.application ?? {};
  const [open, setOpen] = useState(review.decision === "pending");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [decide, { isLoading }] = useDecideFirmCommitteeReviewMutation();

  const decided = review.decision !== "pending";

  const submit = async (decision: "approved" | "rejected") => {
    setError(null);
    if (decision === "rejected" && !remark.trim()) {
      setError("Say why you are returning this to the secretariat.");
      return;
    }
    try {
      await decide({ reviewId: review.id, decision, remark: remark.trim() || undefined }).unwrap();
      setRemark("");
      onDone();
    } catch (e: any) {
      setError(describeApiError(e, "Could not record your decision."));
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50"
      >
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-900 truncate">{app.practice_name ?? "—"}</p>
          <p className="text-xs text-gray-400">
            {app.ownership_structure ?? "Practice firm"} · applied by {app.user?.name ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FirmStatusBadge status={app.status} />
          {DECISION_ICON[review.decision]}
        </div>
      </button>

      {open && (
        <div className="border-t p-5 space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Firm details</h4>
            <Row label="Nature of practice" value={app.nature_of_practice} />
            <Row label="Registered address" value={app.registered_address} />
            <Row label="Firm email" value={app.email} />
            <Row label="Firm phone number" value={app.phone} />
            <Row label="CAC registration number" value={app.cama_reg_number} />
            <Row label="Date of registration with CAC" value={app.cac_reg_date} />
            <Row label="Date of commencement" value={app.commencement_date} />
            <Row label="Ownership structure" value={app.ownership_structure} />
            <Row label="Number of partners / directors" value={app.num_partners} />
            <Row label="Affiliations" value={(app.affiliations ?? []).join(", ") || null} />
            <Row label="Chapter" value={app.chapter?.name} />
            <Row label="Chapter endorsement" value={app.chapter_accept_comment} />
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Partners &amp; directors</h4>
            {(app.partners ?? []).length === 0 && <p className="text-sm text-gray-400">None listed.</p>}
            {(app.partners ?? []).map((p: any) => <FirmPersonCard key={p.id} p={p} />)}
          </div>

          {(app.benevolence_records ?? []).length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-1 text-sm">Acts of benevolence to NIQS</h4>
              {(app.benevolence_records ?? []).map((b: any) => (
                <div key={b.id} className="py-2 border-b last:border-0">
                  {b.type && <p className="text-xs font-medium text-indigo-600">{b.type}</p>}
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{b.description}</p>
                </div>
              ))}
            </div>
          )}

          <div>
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Referees</h4>
            {(app.referees ?? []).length === 0 && <p className="text-sm text-gray-400">None.</p>}
            {(app.referees ?? []).map((r: any) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="text-sm text-gray-900">
                    {[r.member?.surname, r.member?.firstname].filter(Boolean).join(" ") || r.member_id}
                  </p>
                  <p className="text-xs text-gray-400">{r.member?.membership_no}</p>
                </div>
                <span className="text-xs text-gray-500">{r.decision}</span>
              </div>
            ))}
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Registration documents</h4>
            {(app.documents ?? []).length === 0 && <p className="text-sm text-gray-400">None uploaded.</p>}
            {(app.documents ?? []).map((d: any) => {
              const url = firmStorageUrl(d.file_path);
              return (
                <div key={d.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900">{d.doc_type}</p>
                      <p className="text-xs text-gray-400 truncate">{d.file_name}</p>
                    </div>
                  </div>
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline shrink-0">
                      View
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {decided ? (
            <div className="p-3 rounded-lg bg-gray-50 border text-sm text-gray-700">
              <span className="font-medium capitalize">{review.decision}</span>
              {review.decided_at ? ` on ${review.decided_at}` : ""}
              {review.remark && <p className="mt-1 whitespace-pre-wrap">{review.remark}</p>}
            </div>
          ) : (
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message to the secretariat <span className="text-gray-400">(required if returning)</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Your remark…"
              />
              {error && (
                <p className="mt-2 text-sm text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
                </p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => submit("approved")}
                  disabled={isLoading}
                  className="px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60"
                >
                  {isLoading ? "Saving…" : "Approve"}
                </button>
                <button
                  onClick={() => submit("rejected")}
                  disabled={isLoading}
                  className="px-5 py-2.5 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-60"
                >
                  Return to secretariat
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyFirmReviewsPage() {
  const { data: reviews = [], isLoading, isFetching, refetch } = useGetMyFirmCommitteeReviewsQuery();

  const pending = reviews.filter((r: any) => r.decision === "pending");
  const done = reviews.filter((r: any) => r.decision !== "pending");

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Firm Reviews</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Firm registrations assigned to you. Approving sends the applicant forward for interview;
            returning sends the application back to the secretariat with your message.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2.5 bg-white border rounded-xl hover:bg-gray-50 disabled:opacity-50 shrink-0"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {isLoading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : reviews.length === 0 ? (
        <div className="py-20 bg-white border-2 border-dashed border-gray-100 rounded-3xl text-center">
          <Building2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">
            Nothing assigned to you
          </p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest">Awaiting your decision</h2>
              {pending.map((r: any) => <ReviewCard key={r.id} review={r} onDone={refetch} />)}
            </div>
          )}
          {done.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest">Decided</h2>
              {done.map((r: any) => <ReviewCard key={r.id} review={r} onDone={refetch} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
